<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRequest } from '@/composables/useRequest';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
import { useGlobalStore } from '@/stores/global';
import { createVacancySchema } from '@/configs/validation-schemas';

defineProps({
  backgroundColor: {
    type: String,
    default: () => '',
  },
});

const { t } = useI18n();
const globalStore = useGlobalStore();
const vacancySchema = computed(() => createVacancySchema(t));

const loading = ref(false);

const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: vacancySchema,
  initialValues: {
    name: '',
    phone: '',
    question: '',
    file: null,
    page: '',
  },
});

const name = useField('name');
const phone = useField('phone');
const question = useField('question');
const file = useField('file');

const formResponse = ref({});
const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, { action: 'carrier' })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};
const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  const token = await getCaptchaToken();
  const formData = new FormData();
  formData.append('name', values.name);
  formData.append('phone', values.phone);
  formData.append('question', values.question);
  formData.append('file', values.file);
  formData.append('page', currentFullUrl);
  formData.append('token', token ? token : {});

  try {
    const res = await useRequest(`/forms/${formApi['carrier']}`, {
      method: 'POST',
      body: formData,
    });
    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName = t('application_popup.carrier_title');
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: 'carrier',
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }

      resetForm();
      formResponse.value = res;
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});

const fileValue = ref(null);
const error = ref('');

const MAX_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_EXT = ['doc', 'docx', 'txt', 'rtf', 'odt', 'pdf'];

function handleFileSelect(e) {
  const input = e.target;
  const selected = input.files && input.files[0];

  error.value = '';
  fileValue.value = null;
  file.setValue(null);

  if (!selected) return;

  // check format
  const parts = selected.name.split('.');
  const ext = parts.length > 1 ? parts.pop().toLowerCase() : '';
  if (!ALLOWED_EXT.includes(ext)) {
    error.value = $t('vacancy.file_format');
    input.value = '';
    return;
  }

  // check size
  if (selected.size > MAX_SIZE) {
    error.value = $t('vacancy.file_size');
    input.value = '';
    return;
  }

  fileValue.value = selected;
  file.setValue(selected);
}

function clearFile() {
  const input = document.getElementById('file-input');
  if (input) input.value = '';
  fileValue.value = null;
  file.setValue(null);
  error.value = '';
}
</script>

<template>
  <form class="form-vacancy" @submit.prevent="onSubmit">
    <template v-if="formResponse && formResponse?.header">
      <div class="form-subscription__thanks">
        <p class="mb-0 h5">
          {{ formResponse.header || $t('thanks.subscription_success_header') }}
        </p>
        <p v-if="formResponse.content" class="mb-0 body-1-regular thanks__text">
          {{ formResponse.content || $t('thanks.subscription_success_text') }}
        </p>
      </div>
    </template>
    <template v-else>
      <div
        class="form-vacancy__item"
        :class="{ 'form-vacancy__item--error': !!name.errorMessage.value }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <InputText
            id="vacancyName"
            v-model="name.value.value"
            class="form-vacancy__item-field input-transparent"
            :invalid="!!name.errorMessage.value"
            type="text"
          />
          <label
            for="vacancyName"
            class="form-vacancy__item-label"
            :class="{ 'error-100': !!name.errorMessage.value }"
          >
            {{ $t('vacancy.first_name') }}*
          </label>
        </FloatLabel>
      </div>
      <div
        class="form-vacancy__item"
        :class="{ 'form-vacancy__item--error': !!phone.errorMessage.value }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <InputMask
            id="vacancyPhone"
            v-model="phone.value.value"
            class="form-vacancy__item-field input-transparent"
            mask="+380 99 999 9999"
            placeholder="+380"
            fluid
            :invalid="!!phone.errorMessage.value"
          />
          <label
            for="vacancyPhone"
            class="form-vacancy__item-label"
            :class="{ 'error-100': !!phone.errorMessage.value }"
          >
            {{ $t('vacancy.phone') }}*
          </label>
        </FloatLabel>
      </div>
      <div
        class="form-vacancy__item"
        :class="{
          'form-vacancy__item--error': !!question.errorMessage.value,
        }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <Textarea
            id="vacancyQuestion"
            v-model="question.value.value"
            class="form-vacancy__item-field textarea-transparent"
            :invalid="!!question.errorMessage.value"
            rows="1"
            cols="30"
            auto-resize
          />
          <label
            for="vacancyQuestion"
            class="form-vacancy__item-label"
            :class="{ 'error-100': !!question.errorMessage.value }"
          >
            {{ $t('vacancy.question') }}
          </label>
        </FloatLabel>
      </div>
      <div
        class="form-vacancy__item form-vacancy__item-file"
        :class="{
          'form-vacancy__item--error': !!file.errorMessage.value,
        }"
      >
        <div class="form-vacancy__item-file-top">
          <Button v-slot="slotProps" as-child rounded variant="outlined">
            <label
              for="file-input"
              :class="[
                'link-normal button-prev-arrow form-vacancy__item-file-button js-button-vacancy',
                backgroundColor === 'black'
                  ? 'button-transparent'
                  : 'button-transparent-white',
                slotProps.class,
              ]"
            >
              <CIcon
                class="button-prev-arrow__icon"
                :name="
                  backgroundColor === 'black' ? 'file-black' : 'file-white'
                "
                width="16"
                height="16"
              />
              {{ $t('vacancy.attach_cv') }}</label
            >
          </Button>
          <span class="form-vacancy__item-file-note label">{{
            $t('vacancy.allowed_file')
          }}</span>
        </div>
        <div class="form-vacancy__item-file-bottom">
          <input
            id="file-input"
            type="file"
            accept=".doc,.docx,.txt,.rtf,.odt,.pdf"
            hidden
            @change="handleFileSelect"
          />
          <p v-if="fileValue" class="form-vacancy__item-file-chosen">
            <strong>{{ fileValue.name }}</strong>
            <CIcon
              :name="
                backgroundColor === 'black' ? 'close-black' : 'close-white'
              "
              width="16"
              height="16"
              @click="clearFile"
            />
          </p>
          <p v-if="error" class="file-error">
            {{ error }}
          </p>
        </div>
      </div>
      <Button
        :label="$t('vacancy.send_button')"
        type="submit"
        class="form-vacancy__button button-large-danger link-large"
        :loading="loading"
        severity="danger"
        size="large"
      />
      <slot></slot>
    </template>
  </form>
</template>

<style scoped lang="scss">
.form-vacancy {
  &__item {
    &-file {
      padding: 24px 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
      &-top {
        display: flex;
        flex-direction: column;
        gap: 16px;
        @include media-breakpoint-up(sm) {
          align-items: center;
          flex-direction: row;
          gap: 24px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 24);
          @include responsive-spacing(padding-bottom, 24);
          @include responsive-spacing(padding-top, 24);
        }
      }
      &-button {
        max-width: fit-content;
      }
      &-note {
        color: $on-surface-variant;
      }
      &-chosen {
        display: flex;
        flex-direction: row;
        gap: 16px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 16);
        }
      }
    }
  }
  &__button {
    min-height: 80px;
    padding: 12px 32px;
    border-radius: 0 0 4px 4px;
    @include media-breakpoint-up(sm) {
      min-height: 98px;
    }
    @include media-breakpoint-up(md) {
      padding: 4px 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-bottom, 4);
      @include responsive-spacing(padding-left, 32);
      @include responsive-spacing(padding-right, 32);
      @include responsive-spacing(padding-top, 4);
      @include responsive-spacing(border-bottom-left-radius, 4);
      @include responsive-spacing(border-bottom-right-radius, 4);
      @include responsive-spacing(min-height, 98);
    }
  }
}
</style>
